import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash, X, UploadSimple, CheckCircle, Copy } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _adminKeyCounter = 0;
const genKey = () => `ak-${++_adminKeyCounter}-${Math.random().toString(36).slice(2, 6)}`;

export default function AdminPanel() {
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [p, c, u, cust] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/users`),
        axios.get(`${API}/customers`)
      ]);
      setProducts(p.data);
      setCompanies(c.data);
      setUsers(u.data);
      setCustomers(cust.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openModal = (type, item = null) => {
    setModal({ type, item });
    if (type === 'product') {
      const features = item ? (item.features || []) : [];
      setForm(item
        ? { ...item, features, _featureKeys: features.map(() => genKey()) }
        : { name: '', price: '', gst_rate: '', features: [], _featureKeys: [], warranty: '', hsn_code: '' }
      );
    } else if (type === 'company') {
      setForm(item || { name: '', address: '', contact: '', email: '', website: '', logo_url: '', gst_number: '', pan_number: '', quotation_prefix: '', header_image_path: '', footer_image_path: '', bank_name: '', bank_account_number: '', bank_ifsc: '', bank_branch: '' });
    } else if (type === 'customer') {
      setForm(item || { name: '', address: '', contact: '', email: '', gst_number: '' });
    } else {
      setForm(item || { name: '', phone: '' });
    }
  };

  const closeModal = () => { setModal(null); setForm({}); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { type, item } = modal;
      const endpointMap = { product: 'products', company: 'companies', user: 'users', customer: 'customers' };
      const endpoint = `${API}/${endpointMap[type]}`;

      let data;
      if (type === 'product') {
        data = { name: form.name, price: parseFloat(form.price), gst_rate: parseFloat(form.gst_rate), features: form.features || [], warranty: form.warranty || '', hsn_code: form.hsn_code || '' };
      } else if (type === 'company') {
        const { _featureKeys, ...companyData } = form;
        data = companyData;
      } else if (type === 'customer') {
        data = { name: form.name, address: form.address || '', contact: form.contact || '', email: form.email || '', gst_number: form.gst_number || '' };
      } else {
        data = { name: form.name, phone: form.phone || '' };
      }

      if (item) await axios.put(`${endpoint}/${item.id}`, data);
      else await axios.post(endpoint, data);
      fetchAll();
      closeModal();
    } catch (e) { console.error(e); alert('Failed to save'); }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const endpointMap = { product: 'products', company: 'companies', user: 'users', customer: 'customers' };
      await axios.delete(`${API}/${endpointMap[type]}/${id}`);
      fetchAll();
    } catch (e) { alert('Failed to delete'); }
  };

  const handleDuplicateProduct = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/duplicate`);
      fetchAll();
    } catch (e) { alert('Failed to duplicate product'); console.error(e); }
  };

  const handleImageUpload = async (field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/jpg,image/webp';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(field);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm(prev => ({ ...prev, [field]: res.data.path }));
      } catch (err) { alert('Upload failed'); console.error(err); }
      finally { setUploading(''); }
    };
    input.click();
  };

  const handleDocxUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.docx';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading('docx');
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload-docx-letterhead`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm(prev => ({
          ...prev,
          header_image_path: res.data.header_image_path || prev.header_image_path,
          footer_image_path: res.data.footer_image_path || prev.footer_image_path,
        }));
        const parts = [];
        if (res.data.header_image_path) parts.push('header');
        if (res.data.footer_image_path) parts.push('footer');
        if (parts.length > 0) alert(`Extracted ${parts.join(' and ')} image(s) from DOCX`);
        else alert('No images found in the DOCX header/footer. Try uploading images separately.');
      } catch (err) { alert('DOCX processing failed'); console.error(err); }
      finally { setUploading(''); }
    };
    input.click();
  };

  const addFormFeature = () => setForm(prev => ({
    ...prev,
    features: [...(prev.features || []), ''],
    _featureKeys: [...(prev._featureKeys || []), genKey()]
  }));

  const updateFormFeature = (i, val) => {
    const f = [...form.features];
    f[i] = val;
    setForm({ ...form, features: f });
  };

  const removeFormFeature = (i) => {
    const features = [...(form.features || [])];
    features.splice(i, 1);
    const keys = [...(form._featureKeys || [])];
    keys.splice(i, 1);
    setForm({ ...form, features, _featureKeys: keys });
  };

  const tabs = [
    { id: 'products', label: 'Products', count: products.length },
    { id: 'companies', label: 'Companies', count: companies.length },
    { id: 'customers', label: 'Customers', count: customers.length },
    { id: 'users', label: 'Users', count: users.length }
  ];

  return (
    <div>
      <h1 className="text-3xl sm:text-4xl text-stone-900 mb-6" data-testid="admin-panel-title">Administration</h1>

      <div className="flex gap-0 border-b border-stone-200 mb-6 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-5 py-2.5 text-sm font-semibold transition-all border-b-2 -mb-px whitespace-nowrap ${
              tab === t.id ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}>
            {t.label} <span className="ml-1 text-xs text-stone-400">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Products */}
      {tab === 'products' && (
        <Section title="Products" onAdd={() => openModal('product')} addTestId="add-product-button">
          {products.length === 0 ? <Empty /> : (
            <table className="w-full text-sm text-left">
              <thead><tr className="border-b border-stone-200 bg-stone-50">
                <Th>Name</Th><Th>Price</Th><Th>GST %</Th><Th>HSN</Th><Th>Features</Th><Th align="center">Actions</Th>
              </tr></thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <Td bold data-testid={`product-name-${p.id}`}>{p.name}</Td>
                    <Td>Rs. {p.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Td>
                    <Td>{p.gst_rate}%</Td>
                    <Td>{p.hsn_code || '\u2014'}</Td>
                    <Td>{(p.features || []).length} items</Td>
                    <Td align="center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleDuplicateProduct(p.id)} data-testid={`duplicate-product-${p.id}`} title="Duplicate product"
                          className="p-1.5 text-stone-400 hover:text-[#002FA7] hover:bg-stone-100 transition-colors"><Copy size={14} /></button>
                        <button onClick={() => openModal('product', p)} data-testid={`edit-product-${p.id}`}
                          className="p-1.5 text-stone-400 hover:text-[#002FA7] hover:bg-stone-100 transition-colors"><Pencil size={14} /></button>
                        <button onClick={() => handleDelete('product', p.id)} data-testid={`delete-product-${p.id}`}
                          className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash size={14} /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}

      {/* Companies */}
      {tab === 'companies' && (
        <Section title="Companies" onAdd={() => openModal('company')} addTestId="add-company-button">
          {companies.length === 0 ? <Empty /> : (
            <table className="w-full text-sm text-left">
              <thead><tr className="border-b border-stone-200 bg-stone-50">
                <Th>Name</Th><Th>Contact</Th><Th>GST No.</Th><Th>Prefix</Th><Th>Letterhead</Th><Th align="center">Actions</Th>
              </tr></thead>
              <tbody>
                {companies.map(c => (
                  <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <Td bold data-testid={`company-name-${c.id}`}>{c.name}</Td>
                    <Td>{c.contact}</Td>
                    <Td>{c.gst_number}</Td>
                    <Td>{c.quotation_prefix || '\u2014'}</Td>
                    <Td>
                      <div className="flex gap-1">
                        {c.header_image_path && <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 font-medium">Header</span>}
                        {c.footer_image_path && <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 font-medium">Footer</span>}
                        {!c.header_image_path && !c.footer_image_path && <span className="text-stone-400">{'\u2014'}</span>}
                      </div>
                    </Td>
                    <Td align="center">
                      <Actions onEdit={() => openModal('company', c)} onDelete={() => handleDelete('company', c.id)} editTestId={`edit-company-${c.id}`} deleteTestId={`delete-company-${c.id}`} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}

      {/* Customers */}
      {tab === 'customers' && (
        <Section title="Customers" onAdd={() => openModal('customer')} addTestId="add-customer-button">
          {customers.length === 0 ? <Empty /> : (
            <table className="w-full text-sm text-left">
              <thead><tr className="border-b border-stone-200 bg-stone-50">
                <Th>Name</Th><Th>Contact</Th><Th>Email</Th><Th>GST No.</Th><Th align="center">Actions</Th>
              </tr></thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <Td bold data-testid={`customer-name-${c.id}`}>{c.name}</Td>
                    <Td>{c.contact || '\u2014'}</Td>
                    <Td>{c.email || '\u2014'}</Td>
                    <Td>{c.gst_number || '\u2014'}</Td>
                    <Td align="center">
                      <Actions onEdit={() => openModal('customer', c)} onDelete={() => handleDelete('customer', c.id)} editTestId={`edit-customer-${c.id}`} deleteTestId={`delete-customer-${c.id}`} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}

      {/* Users */}
      {tab === 'users' && (
        <Section title="Users" onAdd={() => openModal('user')} addTestId="add-user-button">
          {users.length === 0 ? <Empty /> : (
            <table className="w-full text-sm text-left">
              <thead><tr className="border-b border-stone-200 bg-stone-50">
                <Th>Name</Th><Th>Phone</Th><Th align="center">Actions</Th>
              </tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <Td bold data-testid={`user-name-${u.id}`}>{u.name}</Td>
                    <Td>{u.phone || '\u2014'}</Td>
                    <Td align="center">
                      <Actions onEdit={() => openModal('user', u)} onDelete={() => handleDelete('user', u.id)} editTestId={`edit-user-${u.id}`} deleteTestId={`delete-user-${u.id}`} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 pt-16 px-4 overflow-y-auto">
          <div className="bg-white border border-stone-200 w-full max-w-lg mb-10 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <h3 className="text-base font-bold text-stone-900">
                {modal.item ? 'Edit' : 'Add'} {modal.type.charAt(0).toUpperCase() + modal.type.slice(1)}
              </h3>
              <button onClick={closeModal} className="text-stone-400 hover:text-stone-700"><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {modal.type === 'product' && (
                <>
                  <InputField label="Product Name" testId="modal-product-name" value={form.name} onChange={v => setForm({...form, name: v})} required />
                  <div className="grid grid-cols-3 gap-3">
                    <InputField label="Price (Rs.)" testId="modal-product-price" type="number" step="0.01" value={form.price} onChange={v => setForm({...form, price: v})} required />
                    <InputField label="GST Rate (%)" testId="modal-product-gst" type="number" step="0.01" value={form.gst_rate} onChange={v => setForm({...form, gst_rate: v})} required />
                    <InputField label="HSN Code" testId="modal-product-hsn" value={form.hsn_code} onChange={v => setForm({...form, hsn_code: v})} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Features</label>
                      <button type="button" onClick={addFormFeature} data-testid="modal-add-feature" className="text-xs text-[#002FA7] font-semibold hover:underline">+ Add Feature</button>
                    </div>
                    {(form.features || []).map((f, i) => (
                      <div key={form._featureKeys?.[i] || `fb-${i}`} className="flex items-center gap-2 mb-1.5">
                        <span className="text-stone-400 text-xs">&#8226;</span>
                        <input type="text" value={f} onChange={e => updateFormFeature(i, e.target.value)}
                          className="flex-1 text-sm border border-stone-200 bg-stone-50 px-2.5 py-1.5 focus:border-[#002FA7] outline-none transition-colors" placeholder="Feature description" />
                        <button type="button" onClick={() => removeFormFeature(i)} className="text-stone-300 hover:text-red-500"><X size={14} /></button>
                      </div>
                    ))}
                    {(form.features || []).length === 0 && <p className="text-xs text-stone-400 italic">No features yet</p>}
                  </div>
                  <InputField label="Warranty" testId="modal-product-warranty" value={form.warranty} onChange={v => setForm({...form, warranty: v})} placeholder="e.g., 2 Years onsite warranty" />
                </>
              )}

              {modal.type === 'company' && (
                <>
                  <InputField label="Company Name" testId="modal-company-name" value={form.name} onChange={v => setForm({...form, name: v})} required />
                  <InputField label="Address" testId="modal-company-address" value={form.address} onChange={v => setForm({...form, address: v})} textarea required />
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Contact" testId="modal-company-contact" value={form.contact} onChange={v => setForm({...form, contact: v})} required />
                    <InputField label="Email" testId="modal-company-email" value={form.email} onChange={v => setForm({...form, email: v})} />
                  </div>
                  <InputField label="Website" testId="modal-company-website" value={form.website} onChange={v => setForm({...form, website: v})} />
                  <InputField label="Logo URL" testId="modal-company-logo" value={form.logo_url} onChange={v => setForm({...form, logo_url: v})} />
                  <div className="grid grid-cols-3 gap-3">
                    <InputField label="GST Number" testId="modal-company-gst-number" value={form.gst_number} onChange={v => setForm({...form, gst_number: v})} required />
                    <InputField label="PAN Number" testId="modal-company-pan" value={form.pan_number} onChange={v => setForm({...form, pan_number: v})} />
                    <InputField label="Quote Prefix" testId="modal-company-prefix" value={form.quotation_prefix} onChange={v => setForm({...form, quotation_prefix: v})} placeholder="e.g., VTC" />
                  </div>

                  {/* Letterhead Upload Section */}
                  <div className="border-t border-stone-200 pt-4 mt-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">Letterhead Images (for PDF)</h4>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="text-xs font-semibold text-stone-500 block mb-1.5">Header Image</label>
                        <button type="button" onClick={() => handleImageUpload('header_image_path')} data-testid="upload-header-image"
                          className="w-full border border-dashed border-stone-300 py-3 px-3 text-xs text-stone-500 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors flex items-center justify-center gap-1.5">
                          {uploading === 'header_image_path' ? 'Uploading...' : form.header_image_path ? (
                            <><CheckCircle size={14} className="text-green-600" /> Header uploaded</>
                          ) : (
                            <><UploadSimple size={14} /> Upload header image</>
                          )}
                        </button>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-stone-500 block mb-1.5">Footer Image</label>
                        <button type="button" onClick={() => handleImageUpload('footer_image_path')} data-testid="upload-footer-image"
                          className="w-full border border-dashed border-stone-300 py-3 px-3 text-xs text-stone-500 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors flex items-center justify-center gap-1.5">
                          {uploading === 'footer_image_path' ? 'Uploading...' : form.footer_image_path ? (
                            <><CheckCircle size={14} className="text-green-600" /> Footer uploaded</>
                          ) : (
                            <><UploadSimple size={14} /> Upload footer image</>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="text-center">
                      <span className="text-[10px] text-stone-400 uppercase tracking-wider">or</span>
                    </div>

                    <button type="button" onClick={handleDocxUpload} data-testid="upload-docx-letterhead"
                      className="w-full mt-2 border border-dashed border-stone-300 py-3 text-xs text-stone-500 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors flex items-center justify-center gap-1.5">
                      {uploading === 'docx' ? 'Processing DOCX...' : (
                        <><UploadSimple size={14} /> Upload DOCX letterhead (extract header &amp; footer)</>
                      )}
                    </button>

                    {(form.header_image_path || form.footer_image_path) && (
                      <div className="mt-3 flex gap-2">
                        {form.header_image_path && (
                          <button type="button" onClick={() => setForm({...form, header_image_path: ''})}
                            className="text-[10px] text-red-500 hover:underline">Remove header</button>
                        )}
                        {form.footer_image_path && (
                          <button type="button" onClick={() => setForm({...form, footer_image_path: ''})}
                            className="text-[10px] text-red-500 hover:underline">Remove footer</button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Bank Details */}
                  <div className="border-t border-stone-200 pt-4 mt-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">Bank Details (for Proforma Invoice)</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <InputField label="Bank Name" testId="modal-bank-name" value={form.bank_name} onChange={v => setForm({...form, bank_name: v})} placeholder="e.g., State Bank of India" />
                      <InputField label="Account Number" testId="modal-bank-account" value={form.bank_account_number} onChange={v => setForm({...form, bank_account_number: v})} placeholder="Account number" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <InputField label="IFSC Code" testId="modal-bank-ifsc" value={form.bank_ifsc} onChange={v => setForm({...form, bank_ifsc: v})} placeholder="e.g., SBIN0001234" />
                      <InputField label="Branch" testId="modal-bank-branch" value={form.bank_branch} onChange={v => setForm({...form, bank_branch: v})} placeholder="Branch name" />
                    </div>
                  </div>
                </>
              )}

              {modal.type === 'customer' && (
                <>
                  <InputField label="Customer Name" testId="modal-customer-name" value={form.name} onChange={v => setForm({...form, name: v})} required />
                  <InputField label="Address" testId="modal-customer-address" value={form.address} onChange={v => setForm({...form, address: v})} textarea />
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Contact" testId="modal-customer-contact" value={form.contact} onChange={v => setForm({...form, contact: v})} />
                    <InputField label="Email" testId="modal-customer-email" value={form.email} onChange={v => setForm({...form, email: v})} />
                  </div>
                  <InputField label="GST Number" testId="modal-customer-gst" value={form.gst_number} onChange={v => setForm({...form, gst_number: v})} />
                </>
              )}

              {modal.type === 'user' && (
                <>
                  <InputField label="User Name" testId="modal-user-name" value={form.name} onChange={v => setForm({...form, name: v})} required />
                  <InputField label="Phone" testId="modal-user-phone" value={form.phone} onChange={v => setForm({...form, phone: v})} />
                </>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal} className="flex-1 py-2 text-sm font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 transition-all">Cancel</button>
                <button type="submit" data-testid="modal-submit-button" className="flex-1 py-2 text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002480] transition-all">{modal.item ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, onAdd, addTestId, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-stone-700">{title}</h2>
        <button onClick={onAdd} data-testid={addTestId}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#002FA7] px-4 py-2 hover:bg-[#002480] transition-all active:scale-[0.97]">
          <Plus size={14} weight="bold" /> Add {title.slice(0, -1)}
        </button>
      </div>
      <div className="bg-white border border-stone-200 overflow-x-auto">{children}</div>
    </div>
  );
}

function Th({ children, align }) {
  return <th className={`py-2.5 px-4 font-semibold text-stone-500 text-xs uppercase tracking-wider ${align === 'center' ? 'text-center' : ''}`}>{children}</th>;
}

function Td({ children, bold, align, "data-testid": testId }) {
  return <td className={`py-3 px-4 ${bold ? 'font-semibold text-stone-900' : 'text-stone-600'} ${align === 'center' ? 'text-center' : ''}`} data-testid={testId}>{children}</td>;
}

function Actions({ onEdit, onDelete, editTestId, deleteTestId }) {
  return (
    <div className="flex items-center justify-center gap-1">
      <button onClick={onEdit} data-testid={editTestId} className="p-1.5 text-stone-400 hover:text-[#002FA7] hover:bg-stone-100 transition-colors"><Pencil size={14} /></button>
      <button onClick={onDelete} data-testid={deleteTestId} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash size={14} /></button>
    </div>
  );
}

function Empty() {
  return <div className="py-10 text-center text-stone-400 text-sm">No items found</div>;
}

function InputField({ label, testId, value, onChange, type = 'text', required, textarea, placeholder, step }) {
  const cls = "w-full text-sm border border-stone-200 bg-stone-50 px-3 py-2 focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none transition-all";
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1.5">{label}</label>
      {textarea ? (
        <textarea data-testid={testId} value={value || ''} onChange={e => onChange(e.target.value)} rows="3" className={cls} required={required} placeholder={placeholder} />
      ) : (
        <input data-testid={testId} type={type} step={step} value={value || ''} onChange={e => onChange(e.target.value)} className={cls} required={required} placeholder={placeholder} />
      )}
    </div>
  );
}
